import React from 'react';
import { Hash, BarChart3, Calendar, Star } from 'lucide-react';
import { RealtimeChartDataResponse } from '@/features/realtimeProperty/api/fetchRealtimeProperty';
import { RealtimeCompetitor } from '@/features/realtimeProperty/types';

interface SimpleKPIMetricsProps {
  propertyChartResponse: { data: RealtimeChartDataResponse } | null;
  competitors: RealtimeCompetitor[];
  currencySymbol: string;
}

export const SimpleKPIMetrics: React.FC<SimpleKPIMetricsProps> = ({
  propertyChartResponse,
  competitors,
  currencySymbol
}) => {
  if (!propertyChartResponse?.data) {
    return null;
  }

  const propertyData = propertyChartResponse.data.property;
  const latestData = propertyData.data_points[propertyData.data_points.length - 1];
  const lengthOfStay = propertyChartResponse.data.filters.length_of_stay || 1;
  
  // Calculate competitor average ADR
  const competitorAvgRank = competitors.length > 0
    ? Math.round(competitors.reduce((sum, comp) => {
      const latest = comp.data_points[comp.data_points.length - 1];
      return sum + (latest?.rank || 0);
    }, 0) / competitors.length)
    : 0;

  const competitorAvgADR = competitors.length > 0
    ? Math.round(competitors.reduce((sum, comp) => {
      const latest = comp.data_points[comp.data_points.length - 1];
      const totalPrice = latest?.price || 0;
      return sum + (totalPrice / lengthOfStay);
    }, 0) / competitors.length)
    : 0;

  const ourRank = latestData?.rank?.our || 0;
  const ourTotalPrice = latestData?.price?.our || 0;
  const ourADR = Math.round(ourTotalPrice / lengthOfStay);
  
  const reviewScore = propertyData.propertyFields?.review_score || 0;
  const reviewCount = propertyData.propertyFields?.review_count || 0;
  
  // Calculate availability based on valid data points
  const availableDates = propertyData.data_points.filter(dataPoint => 
    !!dataPoint.price?.our
  ).length;
  const totalDays = propertyChartResponse.data.filters.days;

  // Determine rank performance (lower rank is better)
  const isRankBetter = ourRank > 0 && competitorAvgRank > 0 && ourRank < competitorAvgRank;
  const isRankWorse = ourRank > 0 && competitorAvgRank > 0 && ourRank > competitorAvgRank;

  const metrics = [
    {
      icon: Hash,
      value: `${ourRank}`,
      label: 'Search Rank',
      subtitle: `vs ${competitorAvgRank} competitor avg`,
      valueColorClass: isRankBetter 
        ? 'text-green-600' 
        : isRankWorse 
        ? 'text-red-600' 
        : 'text-gray-900',
    },
    {
      icon: BarChart3,
      value: `${currencySymbol}${ourADR}`,
      label: 'Average ADR',
      subtitle: `vs ${currencySymbol}${competitorAvgADR} competitor avg`,
      valueColorClass: 'text-gray-900',
    },
    {
      icon: Calendar,
      value: `${availableDates}`,
      label: 'Availability',
      subtitle: `For next ${totalDays} days`,
      valueColorClass: 'text-gray-900',
    },
    {
      icon: Star,
      value: reviewScore.toFixed(1),
      label: 'Guest Rating',
      subtitle: `${reviewCount.toLocaleString()} reviews`,
      valueColorClass: 'text-gray-900',
    }
  ];

  return (
    <div className="bg-white border-b border-gray-200 py-3 px-6">
      <div className="flex flex-wrap justify-between items-center gap-4">
        {metrics.map((metric, index) => {
          const IconComponent = metric.icon;
          return (
            <div key={index} className="flex items-center gap-4">
              <div className="p-3 bg-gray-100 rounded-lg flex-shrink-0">
                <IconComponent className="w-6 h-6 text-gray-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-gray-700">
                  {metric.label}
                </div>
                <div className="flex items-baseline gap-2">
                  <span className={`text-2xl font-bold ${metric.valueColorClass}`}>{metric.value}</span>
                  <span className="text-xs text-gray-500">{metric.subtitle}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}